import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Download, Share, X } from "lucide-react";

// No molestamos de nuevo por un rato si el usuario ya cerró el banner —
// ni si acaba de aceptar/rechazar el prompt nativo de Chrome.
const DISMISS_KEY = "ts_install_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 14;

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isMobile() {
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function wasRecentlyDismissed() {
  const raw = localStorage.getItem(DISMISS_KEY);
  const dismissedAt = raw ? parseInt(raw, 10) : NaN;
  if (Number.isNaN(dismissedAt)) return false;
  const days = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return days < DISMISS_COOLDOWN_DAYS;
}

// Banner de "instalar app" — solo en navegador móvil (no dentro del APK ya
// empaquetado, no en desktop, no si ya está instalada). Android/Chrome usa
// el evento real `beforeinstallprompt`; iOS Safari nunca lo dispara, así que
// ahí mostramos instrucciones manuales (Compartir → Agregar a inicio).
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (isStandalone()) return;
    if (!isMobile()) return;
    if (wasRecentlyDismissed()) return;

    if (isIos()) {
      setShowIosHint(true);
      return;
    }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setHidden(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (hidden || (!deferredPrompt && !showIosHint)) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 glass border-t border-white/10 px-5 py-4 flex items-center gap-4 safe-area-bottom"
      data-testid="install-banner"
    >
      <div className="w-10 h-10 rounded-xl bg-[var(--brand-cyan,#FF85AC)]/15 flex items-center justify-center shrink-0">
        <Download className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">Instala la app</p>
        {showIosHint ? (
          <p className="text-xs text-neutral-400 mt-0.5 flex items-center gap-1 flex-wrap">
            Toca <Share className="w-3.5 h-3.5 inline" /> y luego "Agregar a inicio"
          </p>
        ) : (
          <p className="text-xs text-neutral-400 mt-0.5">
            Acceso más rápido, funciona sin conexión
          </p>
        )}
      </div>
      {!showIosHint && (
        <button
          onClick={install}
          className="text-xs uppercase tracking-[0.15em] bg-[var(--brand-cyan,#FF85AC)] text-black font-semibold px-4 py-2 rounded-full shrink-0"
          data-testid="install-banner-accept"
        >
          Instalar
        </button>
      )}
      <button
        onClick={dismiss}
        className="text-neutral-500 hover:text-white p-1 shrink-0"
        aria-label="Cerrar"
        data-testid="install-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
